<script lang="ts">
  import { layout, setView, type View } from '../state/layout.svelte'
  import { prefs, setSidebarOpen } from '../state/prefs.svelte'
  import { setTheme, theme, type ThemeChoice } from '../state/theme.svelte'
  import Icon, { type IconName } from './Icon.svelte'

  /**
   * The desktop's sections (Phase 11 plan §7): the phone's nav, standing up.
   * Forecast, Models and Places, then Theme and Settings at the foot. The
   * chosen section is inverted ink, never a hue.
   *
   * The chosen section pressed again folds the panel away for a map-only
   * window, and any section brings it back (decided in R.4: the mockup has
   * no collapsed panel, but a 1280 px laptop with a 384 px panel is a narrow
   * map, and the old sidebar toggle had users). The choice is the old
   * sidebar's `weather.sidebar`, so it survives a reload.
   */

  const ITEMS: { view: View; label: string; icon: IconName }[] = [
    { view: 'forecast', label: 'Forecast', icon: 'cloud-sun' },
    { view: 'models', label: 'Models', icon: 'chart-line' },
    { view: 'places', label: 'Places', icon: 'star' },
  ]

  /** The same three choices as Settings, in the order a press walks them. */
  const NEXT: Record<ThemeChoice, ThemeChoice> = { system: 'light', light: 'dark', dark: 'system' }
  const NAMES: Record<ThemeChoice, string> = { system: 'System', light: 'Light', dark: 'Dark' }

  function pick(view: View) {
    if (view === layout.view && prefs.sidebarOpen) {
      setSidebarOpen(false)
      return
    }
    setView(view)
    setSidebarOpen(true)
  }

  function chosen(view: View): boolean {
    return prefs.sidebarOpen && layout.view === view
  }
</script>

{#snippet item(view: View, label: string, icon: IconName)}
  <button
    type="button"
    class="touch-target"
    aria-current={chosen(view) ? 'page' : undefined}
    aria-controls="section-panel"
    title={chosen(view) ? `Hide ${label}` : label}
    onclick={() => pick(view)}
  >
    <Icon name={icon} size={22} />
    <span>{label}</span>
  </button>
{/snippet}

<nav class="rail" aria-label="Sections">
  {#each ITEMS as it (it.view)}
    {@render item(it.view, it.label, it.icon)}
  {/each}
  <span class="spacer"></span>
  <button
    type="button"
    class="touch-target"
    aria-label="Theme: {NAMES[theme.choice]}"
    title="Theme: {NAMES[theme.choice]}. Press for {NAMES[NEXT[theme.choice]]}."
    onclick={() => setTheme(NEXT[theme.choice])}
  >
    <Icon name="contrast" size={22} />
    <span aria-hidden="true">Theme</span>
  </button>
  {@render item('settings', 'Settings', 'settings-2')}
</nav>

<style>
  /* 76 px, square, on the float shadow. Its ground is the surface colour,
     one step off the panel's, so the two read as two things in the light
     theme (the redesign README's first open question, answered in R.4). */
  .rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    width: 76px;
    height: 100%;
    padding: 18px 0;
    background: var(--color-surface);
    box-shadow: var(--float-shadow);
  }

  button {
    width: 64px;
    padding: 9px 0 7px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border: 0;
    background: none;
    color: var(--color-text);
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
  }

  @media (hover: hover) {
    button:not([aria-current='page']):hover {
      background: color-mix(in srgb, var(--color-text) 8%, transparent);
    }
  }

  button[aria-current='page'] {
    background: var(--color-text);
    color: var(--color-bg);
    font-weight: 800;
  }

  .spacer {
    flex: 1;
  }
</style>
